import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { cloneDeep } from 'src/ep-utils';
//import { BaseState } from '../base/base-state'
import { EditStatusType, StateHelperService } from '../../../services/proprietary/state-helper.service';
import { ContractObjectDefaults } from './contract-object.defaults';
import { FormDocPackageTypes } from './contract-object.types';
import { AddForm, CloneForm, DeleteForm, RestoreForm, UpdateForm } from './form.actions';
import { AddFormPointer, RemoveFormPointer, UpdateLine } from './line.actions';

export interface FormStateModel {
    form: any;
    currentID: string;
}

@State<FormStateModel>({
    name: 'formState',
    defaults: {
        form: {},
        currentID: null
    }
})
@Injectable()
export class FormState {
    constructor(private helper: StateHelperService) { }

    //******* SELECTORS *********

    @Selector()
    static fullState(state: FormStateModel) {
        return state.form;
    }

    @Selector()
    static currentForm(state: FormStateModel) {
        if (state.form[state.currentID]) {
            return state.form[state.currentID];
        } else {
            return {};
        }
    }

    @Selector()
    static formCountByLineAndType(state: FormStateModel) {
        return (lineId: string, docPackageType: FormDocPackageTypes) => {
            let count = 0;
            for (var i in state.form) {
                if (state.form[i].Line_ID == lineId && state.form[i].Doc_Package_Type == docPackageType) {
                    count++;
                }
            }
            return count;
        };
    }

    @Selector()
    static formsByLineAndType(state: FormStateModel) {
        return (lineId: string, docPackageType: FormDocPackageTypes) => {
            let result: any = [];
            let mandatory = [];
            let others = [];

            for (var i in state.form) {
                if (state.form[i].Line_ID == lineId && state.form[i].Doc_Package_Type == docPackageType) {
                    if (state.form[i].Form_Selection_Method == 'M') {
                        mandatory.push(state.form[i]);
                    } else {
                        others.push(state.form[i]);
                    }
                }
            }

            mandatory.sort((a, b) => a.Form_Object.Collating_Seq - b.Form_Object.Collating_Seq);
            others.sort((a, b) => a.Form_Object.Collating_Seq - b.Form_Object.Collating_Seq);

            for (var i in mandatory) {
                result.push(mandatory[i]);
            }
            for (var i in others) {
                result.push(others[i]);
            }

            return result;
        };
    }

    @Selector()
    static formNameByObjectCode(state: FormStateModel) {
        return (objectCode: string) => {
            let result = '';

            for (var i in state.form) {
                if (state.form[i].Form_Object && state.form[i].Form_Object.Print_Object_Code == objectCode) {
                    return state.form[i].Form_Object.Form_Name;
                }
            }
            return result;
        };
    }

    //******  ACTIONS  ******

    @Action(UpdateForm)
    updateForm(sc: StateContext<FormStateModel>, action: UpdateForm) {
        const state = sc.getState();
        let articleID = action.Form.Article_ID;
        let currentForm = state.form[articleID];
        let lineID = currentForm.Line_ID;
        let updateForm: any = { ...action.Form }; //need DEEP copy?
        this.helper.setItemChanged(updateForm, state.form[articleID].Edit_Status);

        if (updateForm.Edit_Status == EditStatusType.Add) {
            if (currentForm.Selected_Ind != updateForm.Selected_Ind && updateForm.Selected_Ind == 'N') {
                updateForm.Edit_Status = EditStatusType.Available;
            }
        }

        sc.patchState({
            form: {
                ...state.form,
                [articleID]: {
                    ...state.form[articleID],
                    ...updateForm
                }
            }
        });
        sc.dispatch([new UpdateLine({ Line_ID: lineID })]);

        if (updateForm.Edit_Status != EditStatusType.Add && updateForm.Edit_Status != EditStatusType.Available) {
            if (currentForm.Selected_Ind != updateForm.Selected_Ind) {
                if (updateForm.Selected_Ind == 'N') {
                    sc.dispatch([new DeleteForm(articleID)]);
                } else {
                    sc.dispatch([new RestoreForm(articleID)]);
                }
            }
        }
    }

    @Action(DeleteForm)
    deleteForm(sc: StateContext<FormStateModel>, action: DeleteForm) {
        const state = sc.getState();
        let form = { ...state.form }; //shallow copy Form from state
        this.helper.setItemDeleted(form, action.articleID);

        let lineID = form[action.articleID].Line_ID;

        sc.patchState({ form: form });
        if (action.articleID < 0) {
            sc.dispatch(new RemoveFormPointer(lineID, action.articleID.toString()));
        }
        sc.dispatch([new UpdateLine({ Line_ID: lineID })]);
    }

    @Action(RestoreForm)
    restoreForm(sc: StateContext<FormStateModel>, action: RestoreForm) {
        const state = sc.getState();
        let form = { ...state.form }; //shallow copy Form from state
        this.helper.setItemRestored(form, action.articleID);
        form[action.articleID].Selected_Ind = 'Y';

        let lineID = form[action.articleID].Line_ID;
        sc.patchState({ form: form });
        sc.dispatch(new UpdateLine({ Line_ID: lineID }));
    }

    @Action(CloneForm)
    cloneForm(sc: StateContext<FormStateModel>, action: CloneForm) {
        const state = sc.getState();
        let srcID = action.articleID;
        let newID = this.helper.getTempID();
        let newForm = cloneDeep(state.form[srcID]);
        let lineID = newForm.Line_ID;
        this.helper.setItemAdded(newForm);
        newForm.Article_ID = newID;
        sc.patchState({
            currentID: newID,
            form: { ...state.form, [newID]: newForm }
        });
        sc.dispatch([new AddFormPointer(lineID, newID), new UpdateLine({ Line_ID: lineID })]);
    }

    @Action(AddForm)
    addForm(sc: StateContext<FormStateModel>, action: AddForm) {
        const state = sc.getState();

        let formNum = action.Form.Form_Object.Form_Num;
        let docPackage = action.Form.Doc_Package_Type;
        let lineID = action.lineID;

        let duplicateForm = false;

        for (var i in state.form) {
            if (
                state.form[i].Line_ID == lineID &&
                state.form[i].Form_Object.Form_Num == formNum &&
                state.form[i].Doc_Package_Type == docPackage
            ) {
                duplicateForm = true;
                break;
            }
        }

        if (!duplicateForm) {
            let newID = this.helper.getNextTempID(state.form, 'Article_ID');
            let newForm = this.helper.createDefaultItem(ContractObjectDefaults, 'Contract_Form', action.Form);
            this.helper.setItemAdded(newForm);
            newForm.Article_ID = newID;
            newForm.Line_ID = lineID;

            sc.patchState({
                currentID: newID,
                form: { ...state.form, [newID]: newForm }
            });
            sc.dispatch([new AddFormPointer(lineID, newID), new UpdateLine({ Line_ID: lineID })]);
        }
    }
}
